<template>
  <el-form
    label-position="top"
    :model="recipientInfoForm"
    ref="recipientInfoForm"
    :rules="rules"
    label-width="80px"
    size="normal"
  >
    <div test-id="main">
      <p class="font-bold text-xs mb-2" id="bankDetails">
        Mobile Money Details
      </p>
      <hr />
      <el-form-item label="Mobile Money Provider" prop="bankName">
        <el-select
          class="w-full"
          filterable
          remote
          v-model="recipientInfoForm.bankName"
          placeholder="Enter Provider Name"
          :remote-method="
            (query) => remoteMethodBanks(query, recipientInfoForm.bankCountry)
          "
          :loading="loading"
          @change="changeMobileMoneyProvider"
        >
          <el-option
            v-for="(provider, index) in formattedBankOptions"
            :key="index"
            :label="provider.name"
            :value="provider.name"
            @click.native="selectMoneyProviderCode(provider.code)"
          >
            {{ provider.name }}
          </el-option>
          <i slot="prefix" class="el-input__icon el-icon-search"></i>
        </el-select>
      </el-form-item>
      <div
        class="max-w-xl grid grid-cols-12 items-center justify-center gap-x-2"
      >
        <el-form-item label="Phone number" class="col-span-4">
          <el-select
            class="text-primary"
            v-model="recipientInfoForm.countryCode"
          >
            <el-option
              v-for="item in currentCountryCode"
              :key="item.value"
              :label="item.label"
              :value="item.value"
              class="w-full flex items-center"
            >
              <div class="flex items-center gap-x-5">
                <span>{{ item.label }}</span>
              </div>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item class="col-span-8 mt-10 md:mt-10" prop="accountNo">
          <el-input
            min="1"
            @input="changeAccountNumber"
            v-model.trim="recipientInfoForm.accountNo"
            :placeholder="mobileMoneyPhoneNumberPlaceholder"
          ></el-input>
        </el-form-item>
      </div>
      <div
        class="flex flex-col"
        :class="hasProviderCodeOption ? 'md:grid md:grid-cols-4 gap-x-3' : ''"
      >
        <div v-if="hasProviderCodeOption" class="grid col-span-2">
          <el-form-item label="Provider Code" prop="bankCode">
            <el-input
              class="w-full"
              min="1"
              v-model.trim="recipientInfoForm.bankCode"
              placeholder="Enter Provider Code"
              readonly
            ></el-input>
          </el-form-item>
        </div>
        <div :class="hasProviderCodeOption ? 'grid col-span-2' : ''">
          <el-form-item label="Account Name" prop="accountName">
            <el-input
              class="w-full"
              min="1"
              v-model="recipientInfoForm.accountName"
              v-alpha-only
              placeholder="Enter Account Name"
              :readonly="isFallBackAccountName"
              type="text"
            ></el-input>
          </el-form-item>
        </div>
      </div>
      <!-- Recipient Info (XOF) -->
      <div v-if="isFrancDestinationCurrency">
        <p class="font-bold text-xs mt-4 mb-2 userDetails">Recipient Details</p>
        <hr />
        <el-form-item label="Name" prop="recipientName">
          <el-input
            class="w-full"
            v-model="recipientInfoForm.recipientName"
            placeholder="Enter Recipient Name"
          ></el-input>
        </el-form-item>
        <el-form-item label="Country" prop="recipientCountry">
          <el-select
            class="w-full"
            filterable
            v-model="recipientInfoForm.recipientCountry"
            placeholder="Search country"
            @change="changeRecipientCountry"
          >
            <el-option
              v-for="(country, index) in allCountries"
              :key="index"
              :label="country.name"
              :value="country.code2"
              :disabled="country.disabled"
            >
              <el-tooltip
                content="We do not offer service to this country"
                placement="top"
                :disabled="!country.disabled"
              >
                <span>
                  {{ country.name }}
                </span>
              </el-tooltip>
            </el-option>
            <i slot="prefix" class="el-input__icon el-icon-search"></i>
          </el-select>
        </el-form-item>
        <div class="md:grid flex flex-col md:grid-cols-4 gap-x-3">
          <div class="grid col-span-2">
            <el-form-item label="District" prop="recipientDistrict">
              <el-input
                class="w-full"
                v-model="recipientInfoForm.recipientDistrict"
                placeholder="Enter Recipient District"
              ></el-input>
            </el-form-item>
          </div>
          <div class="grid col-span-2">
            <el-form-item label="City" prop="recipientCity">
              <el-input
                class="w-full"
                v-model="recipientInfoForm.recipientCity"
                placeholder="Enter Recipient City"
              ></el-input>
            </el-form-item>
          </div>
        </div>
        <el-form-item label="Address" prop="recipientAddress">
          <el-input
            class="w-full"
            v-model="recipientInfoForm.recipientAddress"
            placeholder="Enter Recipient Address"
          ></el-input>
        </el-form-item>
        <el-form-item label="Phone Number" prop="recipientPhoneNumber">
          <el-input
            class="w-full"
            v-model="recipientInfoForm.recipientPhoneNumber"
            placeholder="Enter Recipient Phone Number"
          ></el-input>
        </el-form-item>
        <el-form-item label="Email" prop="recipientEmail">
          <el-input
            class="w-full"
            v-model.trim="recipientInfoForm.recipientEmail"
            placeholder="Enter Recipient Email"
          ></el-input>
        </el-form-item>
        <el-form-item label="Postal Code" prop="recipientPostalCode">
          <el-input
            class="w-full"
            v-model.trim="recipientInfoForm.recipientPostalCode"
            placeholder="Enter Recipient Zip Code"
          ></el-input>
        </el-form-item>
      </div>
      <el-button
        @click="validateForm('recipientInfoForm')"
        class="bg-primary w-full text-white font-normal rounded-lg py-5 shadow-lg"
        :disabled="isLoading || isSavingBeneficiary"
        :loading="isLoading || isSavingBeneficiary"
      >
        {{ isSavingBeneficiary ? 'Saving' : 'Save' }}
      </el-button>
    </div>
  </el-form>
</template>
<script>
import {
  COUNTRY_CODE_OPTIONS,
  GHS_MONEY_PROVIDER,
  XAF_MONEY_PROVIDERS,
  XOF_MONEY_PROVIDERS,
  PHONE_NUMBER_REGEX,
  ALL_COUNTRIES,
  COUNTRY_SHORTNAMES
} from '@/utils/constants.js'
import country from '@/mixins/getCountryDetails'
import {
  ValidateInput,
  ValidateSwiftCode,
  ValidateText,
  ValidateSingleSpace
} from '@/utils/helpers.js'
import tenant from '@/mixins/tenant.js'
import bulkPayout from '@/mixins/bulkPayout'
export default {
  props: {
    isSavingBeneficiary: {
      type: Boolean,
      required: true
    }
  },
  mixins: [country, tenant, bulkPayout],
  components: {
    // CurrencyIcon: () => import('@/assets/icons/CurrencyIcons.vue'),
    // SenegalIcon: () => import('@/assets/v2/icons/Senegal.vue')
  },
  data () {
    return {
      loading: false,
      countryCodeOptions: COUNTRY_CODE_OPTIONS,
      ghsMoneyProviders: GHS_MONEY_PROVIDER,
      allCountries: ALL_COUNTRIES,
      selectedCountryCode: '',
      isLoading: false,
      recipientInfoForm: {
        countryCode: '',
        accountNo: '',
        bankCode: '',
        accountName: '',
        bankCountry: '',
        country: '',
        recipientName: '',
        recipientAddress: '',
        recipientPostalCode: '',
        recipientState: '',
        recipientCity: '',
        recipientDistrict: '',
        recipientCountry: '',
        recipientPhoneNumber: '',
        recipientEmail: '',
        isMobileMoney: 'yes'
      },
      isCIPhoneNumberLength: 10, // Without country code
      isBJPhoneNumberLength: 8,
      isSNPhoneNumberLength: 9,
      isCMPhoneNumberLength: 9, // Without country code
      rules: {
        accountNo: [
          {
            required: true,
            message: 'Please input account number',
            trigger: ['blur', 'change']
          },
          {
            min: 7,
            message: 'Length should be at least 7 ',
            trigger: ['blur']
          },
          { validator: this.validateAccountNo, trigger: ['blur', 'change'] },
          { validator: ValidateSingleSpace, trigger: ['blur', 'change'] }
        ],
        accountName: [
          {
            required: true,
            message: 'Please input account name',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur'] }
        ],
        countryCode: [
          {
            required: true,
            trigger: ['blur', 'change']
          }
        ],
        recipientName: [
          {
            required: true,
            message: 'Please input recipient first name',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur'] }
        ],
        recipientAddress: [
          {
            required: true,
            message: 'Please input recipient address',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur'] }
        ],
        recipientPhoneNumber: [
          {
            required: true,
            message: 'Please input recipient phone number',
            trigger: ['blur', 'change']
          }
        ],
        recipientEmail: [
          {
            type: 'email',
            message: 'Please input correct email address',
            trigger: ['blur', 'change']
          },
          { validator: ValidateSingleSpace, trigger: ['blur', 'change'] }
        ],
        recipientPostalCode: [
          {
            required: true,
            message: 'Please input recipient zip code',
            trigger: ['blur', 'change']
          },
          { validator: ValidateSwiftCode, trigger: ['blur'] }
        ],
        recipientCountry: [
          {
            required: true,
            message: 'Please select country',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur'] }
        ],
        recipientDistrict: [
          {
            required: true,
            message: 'Please input district name',
            trigger: ['blur', 'change']
          },
          { validator: ValidateInput, trigger: ['blur'] }
        ],
        recipientCity: [
          {
            required: true,
            message: 'Please input city name',
            trigger: ['blur', 'change']
          },
          { validator: ValidateText, trigger: ['blur'] }
        ]
      }
    }
  },
  methods: {
    setDefaultBankCountry () {
      this.recipientInfoForm.countryCode = this?.currentCountryCode[0]?.value
    },
    changeRecipientCountry () {
      this.recipientInfoForm.recipientCity = ''
    },
    validateForm (recipientInfoForm) {
      this.$refs[recipientInfoForm].validate((valid) => {
        if (valid) {
          this.$emit('next', this.recipientInfoForm)
        } else {
          this.$notify.error({
            title: 'Incomplete details',
            message: 'Please fill the missing information',
            position: 'top-right'
          })
        }
      })
    },
    validatePhoneNumber (rule, value, callback) {
      if (!PHONE_NUMBER_REGEX.test(value)) {
        callback(new Error('Please enter a valid number'))
      } else {
        callback()
      }
    },
    selectMoneyProviderCode (code) {
      this.recipientInfoForm.bankCode = code
    },
    changeMobileMoneyProvider () {
      this.recipientInfoForm.countryCode = ''
      if (
        this.computeValidAccountNumberLength(
          this.recipientInfoForm.accountNo.length
        )
      ) {
        this.resolveMoneyProviderAccountName(this.recipientInfoForm.accountNo)
      } else {
        this.recipientInfoForm.accountNo = ''
      }
    },
    async resolveMoneyProviderAccountName (phoneNo) {
      this.resolveAccountNameCore(phoneNo)
    },
    changeAccountNumber () {
      switch (this.recipientInfoForm.bankAccountCountry) {
        case this.$COUNTRY.CI:
          if (this.ciPhoneNumberLength) {
            this.validateMobilePhoneNumber()
          }
          break
        case COUNTRY_SHORTNAMES.BJ:
          if (this.bjPhoneNumberLength) {
            this.validateMobilePhoneNumber()
          }
          break
        case this.$COUNTRY.SN:
          if (this.snPhoneNumberLength) {
            this.validateMobilePhoneNumber()
          }
          break
        case this.$COUNTRY.CM:
          if (this.cmPhoneNumberLength) {
            this.validateMobilePhoneNumber()
          }
          break
        default:
          // Do nothing if no case match
          break
      }
      this.isAccountNameError = ''
    },
    isPhoneNumberLengthValid (expectedLength) {
      return this.recipientInfoForm.accountNo.length === expectedLength
    }
  },
  computed: {
    hasProviderCodeOption () {
      return this.currencyIsGHS
    },
    codeCurrency () {
      return this.payoutCountry?.name || ''
    },
    francMoneyProviders () {
      let providers = []
      if (this.currencyIsXAF) {
        providers = XAF_MONEY_PROVIDERS
      } else if (this.currencyIsXOF) {
        providers = XOF_MONEY_PROVIDERS
      }
      return providers
    },
    isFrancDestinationCurrency () {
      return this.currencyIsXAF || this.currencyIsXOF
    },
    ciPhoneNumberLength () {
      return this.isPhoneNumberLengthValid(this.isCIPhoneNumberLength)
    },
    snPhoneNumberLength () {
      return this.isPhoneNumberLengthValid(this.isSNPhoneNumberLength)
    },
    bjPhoneNumberLength () {
      return this.isPhoneNumberLengthValid(this.isBJPhoneNumberLength)
    },
    cmPhoneNumberLength () {
      return this.isPhoneNumberLengthValid(this.isCMPhoneNumberLength)
    }
  },
  mounted () {
    this.setDefaultBankCountry()
  }
}
</script>
<style scoped lang="scss">
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* For Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}
</style>
